import { RAIL_EUROPE_TRAIN_CATEGORY_TYPE, JOURNEY_TYPE } from '~constants/pointToPointTicket';

import type { OptionalRailEuropeTrainQueryParam } from '~types/queryParam';

interface ReplaceSearchQueriesParams {
  pathname: string;
  queries: OptionalRailEuropeTrainQueryParam;
  selectedCategory: string;
  selectedJourney: string | null;
}

const extractValidParams = (queries: OptionalRailEuropeTrainQueryParam) => {
  const {
    origin,
    destination,
    departureDate,
    arrivalDate,
    ages,
    outboundDepartureTime,
    inboundDepartureTime,
  } = queries;

  const additionalParams: Record<string, string> = {};
  if (origin) additionalParams.origin = origin;
  if (destination) additionalParams.destination = destination;
  if (departureDate) additionalParams.departureDate = departureDate;
  if (arrivalDate) additionalParams.arrivalDate = arrivalDate;
  if (ages) additionalParams.ages = ages;
  if (outboundDepartureTime) additionalParams.outboundDepartureTime = outboundDepartureTime;
  if (inboundDepartureTime) additionalParams.inboundDepartureTime = inboundDepartureTime;

  return additionalParams;
};

/**
 * @description 주어진 path와 url parameter에 따라 유효한 조건만으로 url을 재구성하는 함수
 */
const replaceSearchQueries = ({
  pathname,
  queries,
  selectedCategory,
  selectedJourney,
}: ReplaceSearchQueriesParams): string => {
  const { category: categoryQuery, journey: journeyQuery, ...restQueries } = queries;

  // UTM 파라미터만 추출
  const utmParams = Object.entries(restQueries)
    .filter(([key, value]) => key.startsWith('utm_') && value !== undefined)
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value as string }), {});

  // 카테고리가 PASS인 경우
  if (
    categoryQuery === RAIL_EUROPE_TRAIN_CATEGORY_TYPE.PASS ||
    selectedCategory === RAIL_EUROPE_TRAIN_CATEGORY_TYPE.PASS
  ) {
    const searchParams = new URLSearchParams({
      category: RAIL_EUROPE_TRAIN_CATEGORY_TYPE.PASS,
      ...utmParams,
    });
    return `${pathname}?${searchParams.toString()}`;
  }

  // 카테고리가 TICKET이고 유효한 journey가 있는 경우
  if (
    (categoryQuery === RAIL_EUROPE_TRAIN_CATEGORY_TYPE.TICKET ||
      (!categoryQuery && selectedCategory === RAIL_EUROPE_TRAIN_CATEGORY_TYPE.TICKET)) &&
    (journeyQuery === JOURNEY_TYPE.ONE_WAY || journeyQuery === JOURNEY_TYPE.ROUND_TRIP)
  ) {
    // 유효한 journey가 있고 추가 파라미터가 있으면 현재 URL 유지
    if (Object.keys(restQueries).length > 0) {
      const filteredQueries: Record<string, string> = {};
      Object.entries(queries).forEach(([key, value]) => {
        if (value !== undefined) {
          filteredQueries[key] = value;
        }
      });
      return `${pathname}?${new URLSearchParams(filteredQueries).toString()}`;
    }

    // 유효한 journey만 있으면 현재 URL 유지
    const searchParams = new URLSearchParams({
      category: RAIL_EUROPE_TRAIN_CATEGORY_TYPE.TICKET,
      journey: journeyQuery,
      ...utmParams,
    });
    return `${pathname}?${searchParams.toString()}`;
  }

  // 카테고리가 TICKET이고 journey가 없거나 유효하지 않은 경우
  if (
    categoryQuery === RAIL_EUROPE_TRAIN_CATEGORY_TYPE.TICKET ||
    (!categoryQuery && selectedCategory === RAIL_EUROPE_TRAIN_CATEGORY_TYPE.TICKET)
  ) {
    const validParams = extractValidParams(queries);

    const searchParams = new URLSearchParams({
      category: RAIL_EUROPE_TRAIN_CATEGORY_TYPE.TICKET,
      journey: selectedJourney ?? JOURNEY_TYPE.ONE_WAY,
      ...validParams,
      ...utmParams,
    });
    return `${pathname}?${searchParams.toString()}`;
  }

  // 카테고리가 없거나 유효하지 않은 경우
  if (!categoryQuery || !Object.values(RAIL_EUROPE_TRAIN_CATEGORY_TYPE).includes(categoryQuery)) {
    const validParams = extractValidParams(queries);

    const searchParams = new URLSearchParams({
      category: selectedCategory,
      journey: selectedJourney ?? JOURNEY_TYPE.ONE_WAY,
      ...validParams,
      ...utmParams,
    });
    return `${pathname}?${searchParams.toString()}`;
  }

  // 그 외의 경우는 현재 URL 유지
  const filteredQueries: Record<string, string> = {};
  Object.entries(queries).forEach(([key, value]) => {
    if (value !== undefined) {
      filteredQueries[key] = value;
    }
  });
  return `${pathname}?${new URLSearchParams(filteredQueries).toString()}`;
};

export default replaceSearchQueries;
